// Fetched via get_starter_files (set: "icons") from hg-standards-mcp — see the icon-system standard.
// Copied verbatim: never edit this file inside a repo. Change it in
// hg-standards-mcp/content/starter-files/icons/GenieIcon.tsx, then re-fetch it in every repo.
"use client";

import { createElement, useId, type CSSProperties } from "react";
import {
  GENIE_ICON_PAINT,
  GENIE_ICON_SPARKLE,
  GENIE_ICONS,
  type GenieIconDefinition,
  type GenieIconName,
} from "./genie-icons";

export interface GenieIconProps {
  name: GenieIconName;
  /** Pixel size of the icon square. */
  size?: number;
  /** Primary brand colour, used alone when the gradient is off. Omit for the Help Genie gradient. */
  color?: string;
  /** Second gradient stop. */
  secondaryColor?: string;
  /** Paint a gradient from `color` to `secondaryColor` (needs both). */
  gradientEnabled?: boolean;
  /** CSS gradient angle in degrees. */
  gradientAngle?: number;
  /** Accessible name. Omit for decorative icons next to visible text. */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

const HELP_GENIE_BLUE = "#4E9CFF";
const HELP_GENIE_PINK = "#FF4DB8";

const sparklePath = (x: number, y: number, s: number) => {
  const k = s * 0.22;
  return `M${x} ${y - s}Q${x + k} ${y - k} ${x + s} ${y}Q${x + k} ${y + k} ${x} ${y + s}Q${x - k} ${y + k} ${x - s} ${y}Q${x - k} ${y - k} ${x} ${y - s}Z`;
};

export const GenieIcon = ({
  name,
  size = 24,
  color,
  secondaryColor,
  gradientEnabled = false,
  gradientAngle = 135,
  label,
  className,
  style,
}: GenieIconProps) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const icon: GenieIconDefinition = GENIE_ICONS[name];
  const sparkle = icon.sparkle === false ? null : { ...GENIE_ICON_SPARKLE, ...icon.sparkle };
  const gradientId = `genie-icon-${uid}-g`;
  const maskId = `genie-icon-${uid}-m`;
  const helpGenieGradient = color === undefined;
  const from = color ?? HELP_GENIE_BLUE;
  const to = helpGenieGradient ? HELP_GENIE_PINK : secondaryColor;
  const useGradient = helpGenieGradient || Boolean(gradientEnabled && secondaryColor);
  const paint = useGradient ? `url(#${gradientId})` : from;
  const sparkleCutout = Boolean(sparkle?.cutout);
  const useMask = sparkleCutout || Boolean(icon.knockout?.length);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={paint}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={className}
      style={{ display: "inline-block", flexShrink: 0, ...style }}
    >
      {label && <title>{label}</title>}
      <defs>
        {useGradient && (
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="12"
            x2="24"
            y2="12"
            gradientTransform={`rotate(${gradientAngle - 90} 12 12)`}
          >
            <stop offset="0" stopColor={from} />
            <stop offset="1" stopColor={to} />
          </linearGradient>
        )}
        {useMask && (
          <mask id={maskId} maskUnits="userSpaceOnUse" x="-2" y="-2" width="28" height="28">
            <rect x="-2" y="-2" width="28" height="28" fill="#fff" />
            {sparkle && sparkleCutout && <circle cx={sparkle.x} cy={sparkle.y} r={sparkle.size + 1.5} fill="#000" />}
            {icon.knockout?.map(([tag, attrs], i) =>
              createElement(tag, {
                key: i,
                ...attrs,
                stroke: "#000",
                fill: attrs.fill === GENIE_ICON_PAINT ? "#000" : "none",
              })
            )}
          </mask>
        )}
      </defs>
      <g mask={useMask ? `url(#${maskId})` : undefined}>
        {icon.node.map(([tag, attrs], i) =>
          createElement(tag, { key: i, ...attrs, fill: attrs.fill === GENIE_ICON_PAINT ? paint : undefined })
        )}
      </g>
      {sparkle && <path d={sparklePath(sparkle.x, sparkle.y, sparkle.size)} fill={paint} stroke="none" />}
    </svg>
  );
};

export default GenieIcon;
